import { useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Chip, Input, Switch } from "@andorhealth/clientuireact.designsystem";
import {
  parseRecipients,
  recipientsToJson,
  validateRecipient,
  type CatalogNotificationChannel,
  type RecipientKind,
} from "@/lib/catalog-api";
import { useConfiguration } from "../hooks/useConfiguration";
import { CHANNEL_LABELS } from "../lib/steps";
import { EditingBanner } from "./EditingBanner";

function RecipientChips({
  value,
  onChange,
  placeholder,
  kind,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  kind: RecipientKind;
}) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const commit = () => {
    const next = draft.trim();
    if (!next) {
      setError(null);
      return;
    }
    const validationError = validateRecipient(kind, next);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!value.includes(next)) onChange([...value, next]);
    setDraft("");
    setError(null);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: "wrap" }}>
        {value.map((recipient) => (
          <Chip
            key={recipient}
            label={recipient}
            size="small"
            onDelete={() => onChange(value.filter((item) => item !== recipient))}
            sx={{ borderRadius: "2px" }}
          />
        ))}
      </Stack>
      <Input
        size="small"
        fullWidth
        placeholder={placeholder}
        value={draft}
        error={Boolean(error)}
        helperText={error ?? undefined}
        onChange={(event) => {
          setDraft(event.target.value);
          if (error) setError(null);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === ",") {
            event.preventDefault();
            commit();
          }
        }}
        onBlur={commit}
      />
    </Box>
  );
}

export function ContactsStep() {
  const { policyDraft, setPolicyDraft, channels } = useConfiguration();

  const setTierChannelEnabled = (
    tierIndex: number,
    channel: CatalogNotificationChannel,
    enabled: boolean,
  ) => {
    setPolicyDraft((prev) => {
      const tiers = [...prev.tiers];
      const tier = {
        ...tiers[tierIndex],
        channels: [...tiers[tierIndex].channels],
      };
      const index = tier.channels.findIndex(
        (item) => item.notificationChannelId === channel.notificationChannelId,
      );
      if (enabled && index < 0) {
        tier.channels.push({
          notificationChannelId: channel.notificationChannelId,
          sortOrder: tier.channels.length + 1,
          isRequired:
            channel.channelCode === "EMAIL" || channel.channelCode === "SMS",
          templateKey: `request_tier${tier.tierOrder}_${channel.channelCode.toLowerCase()}`,
          destinationRuleJson: null,
          channelCode: channel.channelCode,
        });
      } else if (!enabled && index >= 0) {
        tier.channels.splice(index, 1);
      }
      tiers[tierIndex] = tier;
      return { ...prev, tiers };
    });
  };

  const updateTierChannel = (
    tierIndex: number,
    channelId: number,
    patch: Partial<(typeof policyDraft.tiers)[number]["channels"][number]>,
  ) => {
    setPolicyDraft((prev) => {
      const tiers = [...prev.tiers];
      tiers[tierIndex] = {
        ...tiers[tierIndex],
        channels: tiers[tierIndex].channels.map((channel) =>
          channel.notificationChannelId === channelId
            ? { ...channel, ...patch }
            : channel,
        ),
      };
      return { ...prev, tiers };
    });
  };

  return (
    <Stack spacing={2.5}>
      <EditingBanner />
      {policyDraft.tiers.map((tier, tierIndex) => (
        <Box key={tier.tierOrder} sx={{ border: "1px solid #e4e7ec", p: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 1.5 }}>
            Step {tier.tierOrder} — {tier.displayName}
          </Typography>
          <Stack spacing={1.5}>
            {channels.map((channel) => {
              const linked = tier.channels.find(
                (item) =>
                  item.notificationChannelId === channel.notificationChannelId,
              );
              return (
                <Box
                  key={channel.notificationChannelId}
                  sx={{ bgcolor: "#f8fafc", p: 1.5 }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(linked)}
                        onChange={(event) =>
                          setTierChannelEnabled(
                            tierIndex,
                            channel,
                            event.target.checked,
                          )
                        }
                      />
                    }
                    label={CHANNEL_LABELS[channel.channelCode] ?? channel.displayName}
                  />
                  {linked ? (
                    <Stack spacing={1} sx={{ mt: 1, ml: 4 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={linked.isRequired}
                            onChange={(event) =>
                              updateTierChannel(
                                tierIndex,
                                channel.notificationChannelId,
                                { isRequired: event.target.checked },
                              )
                            }
                          />
                        }
                        label="Must go through"
                      />
                      {(channel.channelCode === "EMAIL" ||
                        channel.channelCode === "SMS") && (
                        <RecipientChips
                          kind={channel.channelCode}
                          value={parseRecipients(linked.destinationRuleJson)}
                          onChange={(recipients) =>
                            updateTierChannel(
                              tierIndex,
                              channel.notificationChannelId,
                              { destinationRuleJson: recipientsToJson(recipients) },
                            )
                          }
                          placeholder={
                            channel.channelCode === "EMAIL"
                              ? "Email address, then Enter"
                              : "Phone number, then Enter"
                          }
                        />
                      )}
                    </Stack>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
